<template>
  <div class="agreement-wrapper">
    <Card class="agreement-card">
      <template #title>
        <div class="agreement-header">
          <h2 class="agreement-title">Investment Advisory Agreement</h2>
          <p class="agreement-subtitle">SEBI Registered Investment Adviser | Reg. No: INA000020554</p>
        </div>
      </template>

      <template #content>
        <div class="agreement-content">
          <!-- Verified State - Agreement Verified -->
          <div v-if="docSigningStatus === 'Verified'" class="verified-state">
            <div class="verified-icon">
              <i class="pi pi-check-circle"></i>
            </div>
            <h3>Agreement Verified!</h3>
            <p>Your investment advisory agreement has been verified. You can now proceed to payment.</p>

            <div class="success-box">
              <div class="success-row">
                <i class="pi pi-file-check"></i>
                <span>Agreement Signed & Verified</span>
              </div>
              <div class="success-row">
                <i class="pi pi-shield-check"></i>
                <span>SEBI Compliance Complete</span>
              </div>
            </div>

            <ANButton
              label="Proceed to Payment"
              variant="primary"
              icon="pi pi-arrow-right"
              icon-position="right"
              size="large"
              :loading="isTransitioning"
              :disabled="isTransitioning"
              @click="proceedToPayment"
              class="proceed-btn"
            />
          </div>

          <!-- Signed State - Pending Verification -->
          <div v-else-if="docSigningStatus === 'signed'" class="pending-state">
            <div class="pending-icon">
              <i class="pi pi-clock"></i>
            </div>
            <h3>Agreement Signed - Awaiting Verification</h3>
            <p>
              Thank you for signing the agreement! Your document is currently under verification by our team.
              This process typically takes 2-24 hours.
            </p>

            <div class="info-box">
              <div class="info-row">
                <span class="info-label">Status:</span>
                <span class="info-value status-pending">Pending Verification</span>
              </div>
              <div class="info-row">
                <span class="info-label">Signed on:</span>
                <span class="info-value">{{ new Date().toLocaleDateString('en-IN') }}</span>
              </div>
            </div>

            <div class="action-section">
              <ANButton
                label="Check Verification Status"
                variant="primary"
                icon="pi pi-refresh"
                :loading="isCheckingStatus || isTransitioning"
                :disabled="isCheckingStatus || isTransitioning"
                @click="checkDocSigningStatus"
                class="check-btn"
              />
              <p class="support-contact">
                <i class="pi pi-phone"></i>
                For urgent verification, call <strong>+91 9958836060</strong>
              </p>
              <p class="status-note">
                <i class="pi pi-info-circle"></i>
                You will be notified via email once verification is complete. You can also check the status anytime using the button above.
              </p>
            </div>
          </div>

          <!-- Initial State - Not Signed Yet -->
          <div v-else class="initial-state">
          <!-- Info Section -->
          <div class="info-section">
            <div class="info-icon">
              <i class="pi pi-info-circle"></i>
            </div>
            <div class="info-text">
              <h3>Why Sign This Agreement?</h3>
              <p>
                As per SEBI regulations, you must sign the Investment Advisory Agreement before
                accessing personalized investment advisory services. This agreement ensures transparency
                and protects your rights as an investor.
              </p>
            </div>
          </div>

          <!-- Agreement Details -->
          <div class="details-section">
            <h4>What's Included:</h4>
            <ul class="details-list">
              <li>
                <i class="pi pi-check-circle"></i>
                <span>Terms of engagement between you and AssetNivesh</span>
              </li>
              <li>
                <i class="pi pi-check-circle"></i>
                <span>Scope of investment advisory services</span>
              </li>
              <li>
                <i class="pi pi-check-circle"></i>
                <span>Fee structure and payment terms</span>
              </li>
              <li>
                <i class="pi pi-check-circle"></i>
                <span>Your rights and responsibilities as a client</span>
              </li>
              <li>
                <i class="pi pi-check-circle"></i>
                <span>Confidentiality and data protection clauses</span>
              </li>
            </ul>
          </div>

          <!-- Signing Process -->
          <div class="process-section">
            <h4>Signing Process:</h4>
            <div class="process-steps">
              <div class="process-step">
                <div class="step-number">1</div>
                <div class="step-content">
                  <strong>Click "Sign via Aadhaar"</strong>
                  <p>We'll generate your personalized agreement</p>
                </div>
              </div>
              <div class="process-step">
                <div class="step-number">2</div>
                <div class="step-content">
                  <strong>Review the Agreement</strong>
                  <p>Read through the terms in the secure Digio window</p>
                </div>
              </div>
              <div class="process-step">
                <div class="step-number">3</div>
                <div class="step-content">
                  <strong>Sign Electronically</strong>
                  <p>Complete Aadhaar e-Sign authentication</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Action Button -->
          <div class="action-section">
            <ANButton
              label="Sign via Aadhaar e-Sign"
              variant="primary"
              icon="pi pi-file-edit"
              size="large"
              :loading="isLoading || isTransitioning"
              :disabled="isLoading || isTransitioning"
              @click="startSigning"
              class="sign-btn"
            />
            <p class="secure-note">
              <i class="pi pi-shield"></i>
              Secured by Digio - India's leading digital signature platform
            </p>
          </div>
          </div>
          <!-- End of initial-state -->

        </div>
        <!-- End of agreement-content -->
      </template>
    </Card>

    <!-- Digio Sign Modal -->
    <DigioSignModal
      v-if="showModal && signingLink"
      :signingLink="signingLink"
      @close="showModal = false"
      @completed="onSignCompleted"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { useUserStore } from '@/store/user-store';
import Card from 'primevue/card';
import ANButton from '@/components/common/AnButton.vue';
import DigioSignModal from '@/components/dashboard/DigioSignModal.vue';

const store = useUserStore();

const showModal = ref(false);
const signingLink = ref(null);
const isLoading = ref(false);
const docSigningStatus = ref(null); // null, 'signed', 'Verified'
const isCheckingStatus = ref(false);
const isTransitioning = ref(false); // Loading state during state transitions
const isCreatingPayment = ref(false); // NEW: Prevent duplicate payment creation

onMounted(async () => {
  // Initialize from dashboardData if available
  if (store.dashboardData?.agreementStatus) {
    const status = store.dashboardData.agreementStatus;
    docSigningStatus.value = status === 'Not Initiated' ? null : status;
    console.log('Initial doc signing status from dashboardData:', docSigningStatus.value);
  }

  // Then check API for latest status
  await checkDocSigningStatus();
});

// Watch for changes in dashboard data to update status
watch(
  () => store.dashboardData?.docSigningStatus,
  (newStatus) => {
    if (newStatus !== undefined) {
      const processedStatus = newStatus === 'Not Initiated' ? null : newStatus;
      // Always update from dashboardData as it's the source of truth
      docSigningStatus.value = processedStatus;
      console.log('Doc signing status updated from dashboardData:', docSigningStatus.value);
    }
  }
);

async function checkDocSigningStatus() {
  try {
    isCheckingStatus.value = true;
    const response = await store.getDocSigningStatus();
    console.log('Doc signing status API response:', response);

    if (response.success && response.data) {
      const newStatus = response.data.DocSigningStatus;

      // Treat "Not Initiated" as null
      const processedStatus = newStatus === 'Not Initiated' ? null : newStatus;

      console.log('Current doc signing status:', docSigningStatus.value);
      console.log('New doc signing status from API:', processedStatus);

      // Update the status first
      docSigningStatus.value = processedStatus;

      // If status is Verified, trigger auto-redirect to payment
      if (processedStatus === 'Verified') {
        console.log('Agreement is Verified - triggering auto-redirect to payment');
        store.fireAlert('Success', 'Agreement verified! Redirecting to payment...');
        store.dashboardData.agreementStatus = 'Verified'; // Ensure dashboardData is updated
        // Reset flags to ensure proceedToPayment can execute
        isTransitioning.value = false;
        isCreatingPayment.value = false;

        // Add transition delay
        await new Promise(resolve => setTimeout(resolve, 1500)); // 1.5 second delay

        await proceedToPayment();
      }
    }
  } catch (error) {
    console.error('Error checking doc signing status:', error);
  } finally {
    isCheckingStatus.value = false;
  }
}async function startSigning() {
  isLoading.value = true;
  try {
    console.log('Initiating document signing request...');
    const response = await store.createDocSigningRequest({});

    console.log('Document signing response:', response);

    if (response.success && response.data) {
      const signers = response.data.signers;

      // Find the customer signer (reason: "Customer Agreement")
      const customerSigner = signers.find(signer => signer.reason === "Customer Agreement");

      if (customerSigner && customerSigner.authentication_url) {
        signingLink.value = customerSigner.authentication_url;
        showModal.value = true;
        console.log('Opening signing modal with URL:', signingLink.value);
      } else {
        throw new Error('Customer signing URL not found in response');
      }
    } else {
      throw new Error(response.message || 'Failed to create signing request');
    }
  } catch (error) {
    console.error('Error initiating signing:', error);
    store.fireAlert('Error', error.message || 'Failed to initiate document signing. Please try again.');
  } finally {
    isLoading.value = false;
  }
}

async function onSignCompleted() {
  console.log('Document signing completed');
  showModal.value = false;

  // Show transition loading state
  isTransitioning.value = true;

  try {
    // Mark agreement as signed (updates status to "signed" in backend)
    await store.markAgreementAsSigned();

    // Update local status
    docSigningStatus.value = 'signed';

    // Show success message
    store.fireAlert('Success', 'Agreement signed successfully! Awaiting verification.');
  } catch (error) {
    console.error('Error updating agreement status:', error);
    store.fireAlert('Error', 'Failed to update agreement status. Please try again.');
  } finally {
    // Clear transition state after a short delay
    setTimeout(() => {
      isTransitioning.value = false;
    }, 1000);
  }
}

async function proceedToPayment() {
  console.log('proceedToPayment called');

  // Prevent duplicate calls
  if (isCreatingPayment.value || isTransitioning.value) {
    console.log('Payment creation already in progress');
    return;
  }

  try {
    isTransitioning.value = true;
    isCreatingPayment.value = true;

    console.log('Setting tab to PaymentQRCode...');

    // Navigate to payment tab
    store.setTabView('PaymentQRCode');

    console.log('Reloading dashboard data to update sidebar...');

    // Reload dashboard data so sidebar updates with correct docSigningStatus
    await store.loadDashboardData(store.user.email);

    console.log('Navigation complete - current tab:', store.userTab);
  } catch (error) {
    console.error('Error navigating to payment:', error);
    store.fireAlert('Error', 'Failed to proceed to payment. Please try again.');
  } finally {
    isTransitioning.value = false;
    setTimeout(() => {
      isCreatingPayment.value = false;
    }, 1000);
  }
}
</script>

<style scoped>
.agreement-wrapper {
  padding: 24px;
  max-width: 900px;
  margin: 0 auto;
}

.agreement-card {
  box-shadow: var(--an-card-shadow);
}

.agreement-header {
  text-align: center;
  padding-bottom: 16px;
  border-bottom: 2px solid var(--an-primary);
}

.agreement-title {
  color: var(--an-text-primary);
  margin: 0 0 12px 0;
  font-size: 2rem;
  font-weight: 700;
}

.agreement-subtitle {
  color: #28a745;
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
}

.agreement-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding-top: 8px;
}

/* Info Section */
.info-section {
  display: flex;
  gap: 20px;
  padding: 24px;
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-radius: 12px;
  border-left: 4px solid #2196f3;
}

.info-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  background: #2196f3;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
}

.info-text h3 {
  color: #1565c0;
  margin: 0 0 8px 0;
  font-size: 1.2rem;
  font-weight: 600;
}

.info-text p {
  color: #0d47a1;
  margin: 0;
  line-height: 1.6;
}

/* Details Section */
.details-section {
  padding: 24px;
  background: #f8f9fa;
  border-radius: 12px;
}

.details-section h4 {
  color: var(--an-text-primary);
  margin: 0 0 16px 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.details-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.details-list li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  color: var(--an-text-secondary);
  line-height: 1.6;
}

.details-list i {
  color: #28a745;
  font-size: 20px;
  flex-shrink: 0;
  margin-top: 2px;
}

/* Process Section */
.process-section {
  padding: 24px;
  background: #fff3cd;
  border-radius: 12px;
  border-left: 4px solid #ffc107;
}

.process-section h4 {
  color: #856404;
  margin: 0 0 20px 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.process-steps {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.process-step {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.step-number {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  background: #ffc107;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #856404;
  font-weight: 700;
  font-size: 18px;
}

.step-content {
  flex: 1;
}

.step-content strong {
  color: #856404;
  display: block;
  margin-bottom: 4px;
  font-size: 1rem;
}

.step-content p {
  color: #856404;
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
}

/* Action Section */
.action-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
}

.sign-btn {
  min-width: 300px;
}

.secure-note {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6c757d;
  font-size: 0.9rem;
  margin: 0;
  font-weight: 500;
}

.secure-note i {
  color: #28a745;
  font-size: 18px;
}

/* Verified State */
.verified-state {
  text-align: center;
  padding: 40px 20px;
}

.verified-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #d4edda;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
}

.verified-icon i {
  font-size: 48px;
  color: #28a745;
  animation: checkBounce 0.6s ease-out;
}

@keyframes checkBounce {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

.verified-state h3 {
  color: #2c3e50;
  margin: 0 0 12px 0;
  font-size: 24px;
  font-weight: 600;
}

.verified-state p {
  color: #666;
  margin: 0 0 32px 0;
  font-size: 16px;
  line-height: 1.6;
}

.success-box {
  background: #d4edda;
  border-radius: 8px;
  padding: 24px;
  margin: 0 auto 32px;
  max-width: 400px;
  border: 2px solid #28a745;
}

.success-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  color: #155724;
  font-weight: 600;
}

.success-row i {
  color: #28a745;
  font-size: 20px;
}

.success-row:not(:last-child) {
  border-bottom: 1px solid #c3e6cb;
}

.proceed-btn {
  min-width: 300px;
}

/* Pending State */
.pending-state {
  text-align: center;
  padding: 40px 20px;
}

.pending-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #fff3cd;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
}

.pending-icon i {
  font-size: 48px;
  color: #856404;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.pending-state h3 {
  color: #2c3e50;
  margin: 0 0 12px 0;
  font-size: 24px;
  font-weight: 600;
}

.pending-state p {
  color: #666;
  margin: 0 0 24px 0;
  font-size: 16px;
  line-height: 1.6;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
}

.info-box {
  background: #fff3cd;
  border-radius: 8px;
  padding: 20px;
  margin: 0 auto 32px;
  max-width: 400px;
  border: 2px solid #ffc107;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.info-row:not(:last-child) {
  border-bottom: 1px solid #ffe69c;
}

.info-label {
  color: #856404;
  font-weight: 600;
}

.info-value {
  color: #533f03;
  font-weight: 500;
}

.status-pending {
  color: #856404;
}

.check-btn {
  min-width: 300px;
  margin-bottom: 16px;
}

.support-contact {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 12px 0;
  color: #2c3e50;
  font-size: 14px;
  line-height: 1.5;
}

.support-contact i {
  color: #28a745;
  font-size: 16px;
  flex-shrink: 0;
}

.support-contact strong {
  color: #AB3498;
  font-weight: 700;
}

.status-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #e7f3ff;
  border-left: 4px solid #2196f3;
  padding: 12px;
  border-radius: 4px;
  text-align: left;
  color: #004085;
  font-size: 14px;
  line-height: 1.5;
  max-width: 400px;
  margin: 0 auto;
}

.status-note i {
  color: #2196f3;
  font-size: 16px;
  flex-shrink: 0;
  margin-top: 2px;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .agreement-wrapper {
    padding: 20px;
  }

  .agreement-title {
    font-size: 1.75rem;
  }

  .process-steps {
    gap: 14px;
  }

  .sign-btn,
  .proceed-btn,
  .check-btn {
    min-width: 280px;
  }
}

@media (max-width: 768px) {
  .agreement-wrapper {
    padding: 16px;
    max-width: 100%;
  }

  .agreement-header {
    padding-bottom: 12px;
  }

  .agreement-title {
    font-size: 1.5rem;
    line-height: 1.3;
  }

  .agreement-subtitle {
    font-size: 0.85rem;
  }

  .agreement-content {
    gap: 24px;
  }

  /* Info Section */
  .info-section {
    flex-direction: column;
    padding: 16px;
    gap: 16px;
    text-align: center;
  }

  .info-icon {
    margin: 0 auto;
    width: 40px;
    height: 40px;
    font-size: 20px;
  }

  .info-text h3 {
    font-size: 1.1rem;
  }

  .info-text p {
    font-size: 0.9rem;
  }

  /* Details Section */
  .details-section,
  .process-section {
    padding: 16px;
  }

  .details-section h4,
  .process-section h4 {
    font-size: 1rem;
  }

  .details-list {
    gap: 10px;
  }

  .details-list li {
    font-size: 0.9rem;
  }

  .details-list i {
    font-size: 18px;
  }

  /* Process Section */
  .process-steps {
    gap: 12px;
  }

  .step-number {
    width: 32px;
    height: 32px;
    font-size: 16px;
  }

  .step-content strong {
    font-size: 0.95rem;
  }

  .step-content p {
    font-size: 0.85rem;
  }

  /* Action Section */
  .action-section {
    padding: 20px 16px;
    gap: 12px;
  }

  .sign-btn,
  .proceed-btn,
  .check-btn {
    min-width: 100%;
    width: 100%;
  }

  .support-contact {
    font-size: 0.85rem;
  }

  .secure-note,
  .status-note {
    font-size: 0.85rem;
  }

  /* Verified State */
  .verified-state,
  .pending-state {
    padding: 32px 16px;
  }

  .verified-icon,
  .pending-icon {
    width: 64px;
    height: 64px;
    margin-bottom: 20px;
  }

  .verified-icon i,
  .pending-icon i {
    font-size: 36px;
  }

  .verified-state h3,
  .pending-state h3 {
    font-size: 1.5rem;
  }

  .verified-state p,
  .pending-state p {
    font-size: 0.95rem;
    margin-bottom: 24px;
  }

  .success-box,
  .info-box {
    padding: 16px;
    max-width: 100%;
  }

  .success-row {
    padding: 10px 0;
    font-size: 0.9rem;
  }

  .success-row i {
    font-size: 18px;
  }

  .status-note {
    max-width: 100%;
  }
}

@media (max-width: 480px) {
  .agreement-wrapper {
    padding: 12px;
  }

  .agreement-header {
    padding-bottom: 10px;
  }

  .agreement-title {
    font-size: 1.25rem;
  }

  .agreement-subtitle {
    font-size: 0.8rem;
  }

  .agreement-content {
    gap: 20px;
  }

  .info-section,
  .details-section,
  .process-section {
    padding: 12px;
  }

  .info-icon {
    width: 36px;
    height: 36px;
    font-size: 18px;
  }

  .info-text h3 {
    font-size: 1rem;
  }

  .info-text p {
    font-size: 0.85rem;
  }

  .details-section h4,
  .process-section h4 {
    font-size: 0.95rem;
    margin-bottom: 12px;
  }

  .details-list {
    gap: 8px;
  }

  .details-list li {
    font-size: 0.85rem;
    gap: 10px;
  }

  .details-list i {
    font-size: 16px;
  }

  .process-step {
    gap: 12px;
  }

  .step-number {
    width: 28px;
    height: 28px;
    font-size: 14px;
  }

  .step-content strong {
    font-size: 0.9rem;
  }

  .step-content p {
    font-size: 0.8rem;
  }

  .action-section {
    padding: 16px 12px;
  }

  .support-contact {
    font-size: 0.8rem;
    flex-wrap: wrap;
  }

  .secure-note,
  .status-note {
    font-size: 0.8rem;
    gap: 6px;
  }

  .verified-state,
  .pending-state {
    padding: 24px 12px;
  }

  .verified-icon,
  .pending-icon {
    width: 56px;
    height: 56px;
    margin-bottom: 16px;
  }

  .verified-icon i,
  .pending-icon i {
    font-size: 32px;
  }

  .verified-state h3,
  .pending-state h3 {
    font-size: 1.25rem;
  }

  .verified-state p,
  .pending-state p {
    font-size: 0.9rem;
    margin-bottom: 20px;
  }

  .success-box,
  .info-box {
    padding: 12px;
  }

  .success-row,
  .info-row {
    padding: 8px 0;
    font-size: 0.85rem;
  }

  .success-row i {
    font-size: 16px;
  }
}

@media (max-width: 360px) {
  .agreement-wrapper {
    padding: 8px;
  }

  .agreement-title {
    font-size: 1.1rem;
  }

  .agreement-subtitle {
    font-size: 0.75rem;
  }

  .info-section,
  .details-section,
  .process-section,
  .action-section {
    padding: 10px;
  }

  .info-icon {
    width: 32px;
    height: 32px;
    font-size: 16px;
  }

  .info-text h3 {
    font-size: 0.95rem;
  }

  .details-section h4,
  .process-section h4 {
    font-size: 0.9rem;
  }

  .verified-state h3,
  .pending-state h3 {
    font-size: 1.1rem;
  }
}
</style>
